import { Directive, ElementRef, inject, Input, Renderer2 } from '@angular/core';

/**
 * Stamps a stable, privacy-safe action identifier on an interactive element,
 * serving both ends at once:
 *
 * ```html
 * <myre-button myreActionId="modal.delete" … />
 * ```
 *
 * - `data-testid` — a stable selector for E2E tests.
 * - `data-ph-capture-attribute-action` — read by PostHog autocapture and
 *   surfaced as the `action` event property. It survives
 *   `mask_all_element_attributes` because PostHog reads
 *   `data-ph-capture-attribute-*` through a separate, unmasked path.
 *
 * The key must be a static, namespaced string (`module.action`). Never
 * interpolate user/business data into it — the value leaves the browser via
 * analytics, so anything dynamic would defeat the masking that keeps the layer
 * RGPD-safe.
 */
@Directive({ selector: '[myreActionId]' })
export class ActionIdDirective {
  private _elementRef = inject(ElementRef);
  private _renderer = inject(Renderer2);

  @Input()
  public set myreActionId(key: string) {
    const element = this._elementRef.nativeElement;

    if (!key) {
      this._renderer.removeAttribute(element, 'data-testid');
      this._renderer.removeAttribute(
        element,
        'data-ph-capture-attribute-action',
      );

      return;
    }

    this._renderer.setAttribute(element, 'data-testid', key);
    this._renderer.setAttribute(
      element,
      'data-ph-capture-attribute-action',
      key,
    );
  }
}
